import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useUserContext } from '@/auth/UserContext';
import { useHasFamilySubscription } from '@/hooks/useHasFamilySubscription';
import { useIsMobile } from '@/hooks/useIsMobile';
import { buildDiaryQuery } from '@/pages/Diary/hooks/useDiary';
import { useDiaryDateRange } from '@/pages/Diary/hooks/useDiaryDateRange';
import { DiaryStepDirection } from '@/types/components';
import type { DiaryEntry } from '@/types/diary';
import {
  addDays,
  formatDateWithWeekday,
  formatWeekRange,
  isSameApiDate,
  parseLocalDate,
  toApiDate,
} from '@/utils/date';
import { isHiddenEmptyWeekend } from '@/utils/diary';

const FETCHED_EXTRA_DAYS = 3;
const DESKTOP_DAYS_COUNT = 2;

interface DiaryAnchor {
  date: Date;
  direction: DiaryStepDirection;
}

const getFetchedRange = ({ date, direction }: DiaryAnchor) =>
  direction === DiaryStepDirection.Backward
    ? { start: addDays(date, -FETCHED_EXTRA_DAYS), end: date }
    : { start: date, end: addDays(date, FETCHED_EXTRA_DAYS) };

interface UseHomeDiaryResult {
  isDiaryAvailable: boolean;
  isLoading: boolean;
  desktopEntries: DiaryEntry[];
  mobileEntry: DiaryEntry | undefined;
  pickerLabel: string;
  pickerValue: Date;
  minDate: Date;
  maxDate: Date;
  selectDate: (date: Date) => void;
  goToPrevious?: () => void;
  goToNext?: () => void;
}

export const useHomeDiary = (): UseHomeDiaryResult => {
  const { user } = useUserContext();
  const hasSubscription = useHasFamilySubscription();
  const isMobile = useIsMobile();
  const { from, to, defaultSelectedDate } = useDiaryDateRange();
  const [pickedAnchor, setPickedAnchor] = useState<DiaryAnchor>();

  const isDiaryAvailable = hasSubscription && !!user?.class;
  const isPickedAnchorBrowsable =
    !!pickedAnchor && pickedAnchor.date >= from && pickedAnchor.date <= to;
  const anchor = isPickedAnchorBrowsable
    ? pickedAnchor
    : { date: defaultSelectedDate, direction: DiaryStepDirection.Forward };
  const isSteppingBackward =
    !isMobile && anchor.direction === DiaryStepDirection.Backward;
  const fetchedAnchor = isSteppingBackward
    ? anchor
    : { date: anchor.date, direction: DiaryStepDirection.Forward };

  const { data: entries = [], isLoading } = useQuery({
    ...buildDiaryQuery(getFetchedRange(fetchedAnchor)),
    enabled: isDiaryAvailable,
  });

  const browsableEntries = entries.filter((entry) => {
    const date = parseLocalDate(entry.date);

    return date >= from && date <= to && !isHiddenEmptyWeekend(entry);
  });
  const desktopEntries = isSteppingBackward
    ? browsableEntries.slice(-DESKTOP_DAYS_COUNT)
    : browsableEntries.slice(0, DESKTOP_DAYS_COUNT);
  const firstDesktopEntry = desktopEntries.at(0);
  const lastDesktopEntry = desktopEntries.at(-1);
  const firstShownDate = firstDesktopEntry
    ? parseLocalDate(firstDesktopEntry.date)
    : anchor.date;
  const lastShownDate = lastDesktopEntry
    ? parseLocalDate(lastDesktopEntry.date)
    : addDays(anchor.date, 1);
  const mobileEntry = entries.find((entry) =>
    isSameApiDate(entry.date, toApiDate(anchor.date)),
  );

  const selectDate = (date: Date) =>
    setPickedAnchor({ date, direction: DiaryStepDirection.Forward });

  const mobileLabel = formatDateWithWeekday(toApiDate(anchor.date));
  const desktopLabel = formatWeekRange(firstShownDate, lastShownDate);
  const pickerValue = isMobile ? anchor.date : firstShownDate;
  const previousAnchor: DiaryAnchor = isMobile
    ? { date: addDays(anchor.date, -1), direction: DiaryStepDirection.Forward }
    : {
        date: addDays(firstShownDate, -1),
        direction: DiaryStepDirection.Backward,
      };
  const nextAnchor: DiaryAnchor = {
    date: addDays(isMobile ? anchor.date : lastShownDate, 1),
    direction: DiaryStepDirection.Forward,
  };
  const hasPrevious = previousAnchor.date >= from;
  const hasNext = nextAnchor.date <= to;

  return {
    isDiaryAvailable,
    isLoading,
    desktopEntries,
    mobileEntry,
    pickerLabel: isMobile ? mobileLabel : desktopLabel,
    pickerValue,
    minDate: from,
    maxDate: to,
    selectDate,
    goToPrevious: hasPrevious
      ? () => setPickedAnchor(previousAnchor)
      : undefined,
    goToNext: hasNext ? () => setPickedAnchor(nextAnchor) : undefined,
  };
};
